import type { ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { cn } from '@core/utils/cn';
import { IconButton } from '@ui/IconButton';
import { Icon } from '@ui/Icon';

/**
 * Dialog shell primitives, styled to match the Material dialogs.
 *
 * Layout mirrors `mat-dialog-title` / `mat-dialog-content` / `mat-dialog-actions`, and
 * the header/content/actions padding uses the effective values from styles.scss's
 * override block (9px 14px header, 12px 14px content, 10px 14px actions).
 *
 * `z-[1200]` reproduces the CDK overlay container's z-index so a dialog opened over
 * the sticky page header stacks identically. It is set here, once, so no feature
 * component ever has to think about it.
 */

export function DialogOverlay({ className }: { className?: string }) {
  return (
    <RadixDialog.Overlay
      className={cn(
        'fixed inset-0 z-[1200] bg-black/32',
        'data-[state=open]:animate-[vm-overlay-in_150ms_ease-out]',
        'data-[state=closed]:animate-[vm-overlay-out_120ms_ease-in]',
        className,
      )}
    />
  );
}

interface DialogPanelProps {
  children: ReactNode;
  width?: number;
  maxWidthVw?: number;
  maxHeightVh?: number;
  panelClass?: string;
  /** Forwarded to Radix so `disableClose` can preventDefault these. */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: Event) => void;
  onInteractOutside?: (event: Event) => void;
  onOpenAutoFocus?: (event: Event) => void;
  /** Accessible name when the dialog has no visible DialogTitle. */
  ariaLabel?: string;
  describedBy?: string;
}

export function DialogPanel({
  children,
  width = 400,
  maxWidthVw = 95,
  maxHeightVh = 90,
  panelClass,
  ariaLabel,
  describedBy,
  ...handlers
}: DialogPanelProps) {
  return (
    <RadixDialog.Content
      aria-label={ariaLabel}
      aria-describedby={describedBy}
      className={cn(
        'fixed top-1/2 left-1/2 z-[1200] flex -translate-x-1/2 -translate-y-1/2 flex-col',
        'overflow-hidden rounded-md bg-surface shadow-modal outline-none',
        'data-[state=open]:animate-[vm-content-in_170ms_cubic-bezier(0.16,1,0.3,1)]',
        'data-[state=closed]:animate-[vm-content-out_120ms_ease-in]',
        panelClass,
      )}
      style={{
        width,
        maxWidth: `${maxWidthVw}vw`,
        maxHeight: `${maxHeightVh}vh`,
      }}
      {...handlers}
    >
      {children}
    </RadixDialog.Content>
  );
}

interface DialogHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: string;
  iconClassName?: string;
  /** Renders a close affordance. Omit when `disableClose` is set. */
  onClose?: () => void;
  className?: string;
  /** Set false when the header is reused in a route page instead of a Radix dialog. */
  withinDialog?: boolean;
}

export function DialogHeader({
  title,
  subtitle,
  icon,
  iconClassName,
  onClose,
  className,
  withinDialog = true,
}: DialogHeaderProps) {
  const titleClassName = 'm-0 truncate text-[13.5px] leading-tight font-semibold text-fg';
  const subtitleClassName = 'm-0 mt-0.5 truncate text-xs text-fg-3';
  return (
    <div
      className={cn(
        'flex flex-none items-center gap-3 border-b border-line bg-surface-2 px-3.5 py-2.5',
        className,
      )}
    >
      {icon && <Icon size="md" className={cn('text-accent', iconClassName)}>{icon}</Icon>}
      <div className="min-w-0 flex-1">
        {withinDialog ? (
          <RadixDialog.Title className={titleClassName}>{title}</RadixDialog.Title>
        ) : (
          <h2 className={titleClassName}>{title}</h2>
        )}
        {subtitle &&
          (withinDialog ? (
            <RadixDialog.Description className={subtitleClassName}>{subtitle}</RadixDialog.Description>
          ) : (
            <p className={subtitleClassName}>{subtitle}</p>
          ))}
      </div>
      {onClose && (
        <IconButton label="Close dialog" onClick={onClose}>
          <Icon>close</Icon>
        </IconButton>
      )}
    </div>
  );
}

export function DialogBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('min-h-0 flex-1 overflow-y-auto px-3.5 py-3', className)}>{children}</div>
  );
}

export function DialogFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-none items-center justify-end gap-2 border-t border-line bg-surface-2 px-3.5 py-2.5',
        className,
      )}
    >
      {children}
    </div>
  );
}
